import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, KeyRound, Check } from 'lucide-react';
import AuthShell from './AuthShell';
import AuthHeader from './AuthHeader';
import AuthField from './AuthField';
import AuthError from './AuthError';
import GoogleIcon from './GoogleIcon';

// Presentational content for every Login view state. Login.js (the
// controller) owns all state/handlers and Firebase/session calls; this
// component only renders markup and forwards DOM events upward untouched.
export default function LoginView({
  view, email, onEmailChange, passwordData, onPasswordFieldChange,
  resetEmail, onResetEmailChange,
  loading, error, notice, googleEnabled,
  onSendMagicLink, onPasswordSubmit, onGoogleLogin,
  onGoToPasswordReset, onSendPasswordReset, goTo,
}) {
  const footer = (
    <>
      <p className="auth-footer-line">
        Pas encore de compte ?{' '}
        <Link to="/register">S'inscrire gratuitement</Link>
      </p>
      <Link to="/" className="auth-home-link">
        <ArrowLeft size={14} aria-hidden="true" /> Retour à l'accueil
      </Link>
    </>
  );

  return (
    <AuthShell footer={footer}>
      <AuthError message={notice} tone="info" />
      <AuthError message={error} tone="error" />

      {view === 'completing' && (
        <div className="auth-status">
          <span className="spinner" aria-hidden="true" />
          <p>Connexion en cours…</p>
        </div>
      )}

      {view === 'choice' && (
        <>
          <AuthHeader
            eyebrow="BON RETOUR"
            title="Heureux de vous revoir."
            subtitle="Connectez-vous pour retrouver votre activité NSARA."
          />
          <div className="auth-stack">
            <button type="button" className="auth-button auth-button-primary" disabled={loading} onClick={() => goTo('email-entry')}>
              <Mail size={18} aria-hidden="true" /> Continuer avec votre e-mail
            </button>
            {googleEnabled && (
              <button type="button" className="auth-button auth-button-secondary" disabled={loading} onClick={onGoogleLogin}>
                <GoogleIcon /> Continuer avec Google
              </button>
            )}
            <div className="auth-divider">ou</div>
            <button type="button" className="auth-button auth-button-ghost" disabled={loading} onClick={() => goTo('password')}>
              <KeyRound size={14} aria-hidden="true" /> Se connecter avec un mot de passe
            </button>
          </div>
        </>
      )}

      {view === 'email-entry' && (
        <>
          <AuthHeader
            eyebrow="CONNEXION PAR E-MAIL"
            title="Votre e-mail"
            subtitle="Nous vous enverrons un lien pour vous connecter à NSARA, sans mot de passe à saisir."
          />
          <form onSubmit={onSendMagicLink}>
            <AuthField
              id="login-magic-email"
              label="Adresse e-mail"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={onEmailChange}
              placeholder="votre@email.com"
              autoFocus
            />
            <div className="auth-stack">
              <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
                {loading ? 'Envoi…' : 'Envoyer le lien de connexion'}
              </button>
              <button type="button" className="auth-button auth-button-ghost" onClick={() => goTo('choice')}>
                <ArrowLeft size={14} aria-hidden="true" /> Autre méthode de connexion
              </button>
            </div>
          </form>
        </>
      )}

      {view === 'email-sent' && (
        <div className="auth-confirm">
          <span className="auth-success-icon" aria-hidden="true"><Mail size={24} /></span>
          <h2>Vérifiez votre boîte mail</h2>
          <p>
            Nous avons envoyé un lien de connexion à <strong>{email}</strong>. Ouvrez-le depuis cet appareil pour accéder à votre espace.
          </p>
          <button type="button" className="auth-button auth-button-ghost" onClick={() => goTo('choice')}>
            Utiliser une autre méthode
          </button>
        </div>
      )}

      {view === 'password' && (
        <>
          <AuthHeader eyebrow="MOT DE PASSE" title="Connexion avec mot de passe" />
          <form onSubmit={onPasswordSubmit}>
            <AuthField
              id="login-password-email"
              label="Adresse e-mail"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={passwordData.email}
              onChange={onPasswordFieldChange('email')}
              placeholder="votre@email.com"
            />
            <AuthField
              id="login-password"
              label="Mot de passe"
              type="password"
              autoComplete="current-password"
              value={passwordData.password}
              onChange={onPasswordFieldChange('password')}
              placeholder="Votre mot de passe"
            />
            <div className="auth-forgot-link">
              <button type="button" className="auth-link-button" onClick={onGoToPasswordReset}>
                Mot de passe oublié ?
              </button>
            </div>
            <div className="auth-stack">
              <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
              <button type="button" className="auth-button auth-button-ghost" onClick={() => goTo('choice')}>
                <ArrowLeft size={14} aria-hidden="true" /> Autre méthode de connexion
              </button>
            </div>
          </form>
        </>
      )}

      {view === 'password-reset' && (
        <>
          <AuthHeader
            eyebrow="RÉCUPÉRATION DU COMPTE"
            title="Réinitialiser votre mot de passe"
            subtitle="Saisissez votre adresse e-mail. Nous vous enverrons un lien pour choisir un nouveau mot de passe."
          />
          <form onSubmit={onSendPasswordReset}>
            <AuthField
              id="login-reset-email"
              label="Adresse e-mail"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={resetEmail}
              onChange={onResetEmailChange}
              placeholder="votre@email.com"
              autoFocus
            />
            <div className="auth-stack">
              <button type="submit" className="auth-button auth-button-primary" disabled={loading}>
                {loading ? 'Envoi…' : 'Envoyer le lien'}
              </button>
              <button type="button" className="auth-button auth-button-ghost" onClick={() => goTo('password')}>
                <ArrowLeft size={14} aria-hidden="true" /> Retour à la connexion
              </button>
            </div>
          </form>
        </>
      )}

      {view === 'password-reset-sent' && (
        <div className="auth-confirm">
          <span className="auth-success-icon" aria-hidden="true"><Check size={24} /></span>
          <h2>Consultez votre boîte e-mail</h2>
          <p>
            Si un compte correspond à cette adresse, vous recevrez les instructions pour réinitialiser votre mot de passe.
          </p>
          <button type="button" className="auth-button auth-button-ghost" onClick={() => goTo('password')}>
            Retour à la connexion
          </button>
        </div>
      )}
    </AuthShell>
  );
}
